import {
  ErrorModal,
  MesfModal,
  dxtToLocalServerTime,
  getMomentTz,
} from "@dexteel/mesf-core";
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { get, isNil } from "lodash-es";
import { ChangeEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import {
  linkCasterProcess,
  searchUnlinkedHeats,
} from "../../../../caster/processes/repositories/CasterRepository";
import { useDataEntryContext } from "../../context/reducer.provider";
import { useTorchesDataEntryActions } from "../../hooks/useTorchesDataEntryActions";
import { UnLinkedHeat } from "../../models/unlinkedHeat";

const moment = getMomentTz();
type SelectHeat = {
  heatId: UnLinkedHeat | null;
};
type Props = {
  show: boolean;
  onHide: (shouldUpdate: boolean) => void;
};
export const LinkCasterProcess = ({ show, onHide }: Props) => {
  const { refreshData } = useTorchesDataEntryActions();
  const { state, actions } = useDataEntryContext();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [unlinkedHeats, setUnLinkedHeats] = useState<UnLinkedHeat[]>([]);
  const [selectedHeat, setSelectedHeat] = useState<any>(null);
  const [searchText, setSearchText] = useState("");

  const [error, setError] = useState("");
  //const [showWarningModal, setShowWarningModal] = useState(false);

  const process = state.deProcess?.process;
  const title = `Link Caster Process / Ladle Open: ${dxtToLocalServerTime(
    `${moment.utc(process?.LadleOpen)}`,
    "HH:mm",
  )}`;

  const { handleSubmit, control, setValue, reset } = useForm<SelectHeat>({});

  const onSubmit = async () => {
    if (process) {
      if (isNil(process)) return;

      const response = await linkCasterProcess(
        process.ProcessId,
        selectedHeat.HeatId,
      );
      if (response.ok) {
        actions.setNotificationMessage("Process linked successfully");
        await refreshData();
      } else {
        setError(response.message);
      }
    } else {
      setError("Process not found");
    }
    onHide(true);
  };

  const handleSearchChange = async () => {
    if (!process) return;
    const heats = await searchUnlinkedHeats(
      searchText,
      process.LadleOpen as Date,
    );
    if (heats.ok) {
      setUnLinkedHeats(get(heats, "data.tables[0].rows", []));
    }
  };

  const handleInputChange = (event: ChangeEvent<{}>, value: string) => {
    setSearchText(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchChange();
    }
  };

  const handleChange = (
    event: ChangeEvent<{}>,
    newValue: UnLinkedHeat | null,
  ) => {
    setSelectedHeat(newValue as any);
    setValue("heatId", newValue, { shouldValidate: true });
  };

  useEffect(() => {
    if (show && process) {
      setIsSubmitLoading(false);
      (async () => {
        setSelectedHeat(null);
        setIsLoadingData(true);
        const heats = await searchUnlinkedHeats("", process.LadleOpen as Date);
        if (heats.ok) {
          setUnLinkedHeats(get(heats, "data.tables[0].rows", []));
        }
        setIsLoadingData(false);
      })();
    } else {
      reset();
    }
  }, [show, process]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            maxWidth="sm"
            open={show}
            handleClose={() => {
              onHide(true);
            }}
            id="linkEAFProcess"
            title={title}
          >
            {isLoadingData ? (
              <>
                <Grid
                  container
                  style={{
                    padding: "60px 280px",
                  }}
                  justifyContent="center"
                  alignContent="center"
                >
                  <Grid item>
                    <CircularProgress />
                  </Grid>
                </Grid>
              </>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <MesfModal.Content
                  style={{
                    padding: "15px 30px",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item md={2}>
                      <Typography
                        style={{
                          margin: 0,
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          height: "56px",
                        }}
                        variant="h6"
                      >
                        To Heat
                      </Typography>
                    </Grid>
                    <Box style={{ height: 70 }}>
                      <Grid item md={10}>
                        <Controller
                          name="heatId"
                          control={control}
                          rules={{ required: "Please select a heat" }}
                          render={({ field, fieldState: { error } }) => (
                            <Autocomplete
                              {...field}
                              options={unlinkedHeats}
                              getOptionLabel={(option) => option.HeatName}
                              value={selectedHeat}
                              onChange={handleChange}
                              inputValue={searchText}
                              onInputChange={handleInputChange}
                              renderOption={(props, option) => (
                                <li
                                  {...props}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <span>{option.HeatName}</span>
                                  {option.IsLinked === 1 && (
                                    <span
                                      style={{
                                        color: "red",
                                        marginLeft: "10px",
                                      }}
                                    >
                                      (linked)
                                    </span>
                                  )}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Search Heat"
                                  variant="outlined"
                                  fullWidth
                                  onKeyDown={handleKeyDown}
                                  error={!!error?.message}
                                  helperText={error ? error.message : null}
                                  slotProps={{
                                    input: {
                                      ...params.InputProps,
                                      style: {
                                        height: "100%",
                                        minWidth: "400px",
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          )}
                        />
                      </Grid>
                    </Box>
                  </Grid>
                </MesfModal.Content>
                <MesfModal.Actions style={{ padding: "20px " }}>
                  <FooterModalButtons
                    isSubmitLoading={isSubmitLoading}
                    onHide={onHide}
                  />
                </MesfModal.Actions>
              </form>
            )}
          </MesfModal>
        </Grid>
      </Grid>

      {/* <MesfModal
				open={showWarningModal}
				handleClose={() => setShowWarningModal(false)}
				title="Warning"
				id="warningModal"
			>
				<MesfModal.Content>
					<p>
						This heat is already linked in another process. Do you want to
						proceed?
					</p>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button
						fullWidth
						variant="contained"
						color="inherit"
						onClick={() => setShowWarningModal(false)}
					>
						Cancel
					</Button>
					<Button
						fullWidth
						variant="contained"
						color="primary"
						onClick={async () => {
							setShowWarningModal(false);
							await onSubmit(); // Proceed with the linking process
						}}
					>
						Proceed
					</Button>
				</MesfModal.Actions>
			</MesfModal>*/}
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
